/* eslint-disable id-match */
import dayjs from 'dayjs';
import compact from 'lodash/compact';
import map from 'lodash/map';
import Cookies from 'react-cookies';
import axios from '../components/_generic/axiosSplunk/axiosSplunk';
import COOKIES from '../config/cookies/cookies';
import {
  URLPAYMENTINSTRUMENTS,
  URLKLARNASESSION,
  URLKLARNAPAYMENT,
} from '../config/setup/setup';
import {
  getWalletAmount,
  getDeliveryOptionType,
  getOrderLinesPayload,
} from './general';
import httpCommonHeaders from './httpCommonHeaders';
import { formatDateForApi } from './productsService';
import { getUserLocationShortName } from './user';

const PAY_TIMEOUT = 40_000;
const sessionSource = Cookies.load(COOKIES.sessionSource);

export const getPaymentInstrument = async (dealId) => {
  if (!dealId) return null;
  const headers = httpCommonHeaders();
  headers.ALT_PAYMENT_METHODS = 'GOOGLE_PAY,APPLE_PAY';
  try {
    const response = await axios({
      cache: false,
      headers,
      method: 'GET',
      url: `${URLPAYMENTINSTRUMENTS}/${dealId}/paymentInstruments`,
      withCredentials: true,
    });
    if (response?.data?.data) {
      return response?.data?.data;
    }

    return null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const createClientSession = async (data) => {
  if (!data) return null;
  try {
    const response = await axios({
      cache: false,
      data,
      headers: httpCommonHeaders(),
      method: 'POST',
      url: URLKLARNASESSION,
      withCredentials: true,
    });
    if (response?.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const updateClientSession = async ({
  consent = false,
  data,
  sessionId,
}) => {
  if (!sessionId || !data) return false;
  try {
    const response = await axios({
      cache: false,
      data,
      headers: httpCommonHeaders(),
      method: 'PUT',
      url: `${URLKLARNASESSION}/${sessionId}${consent ? `?consent=true` : ''}`,
      withCredentials: true,
    });
    if (response.status === 204) {
      return true;
    }

    return false;
  } catch (error) {
    console.error(error);

    return false;
  }
};

export const sendKlarnaPayment = async (data) => {
  try {
    const response = await axios({
      cache: false,
      data,
      headers: httpCommonHeaders(),
      method: 'POST',
      timeout: PAY_TIMEOUT,
      url: URLKLARNAPAYMENT,
      withCredentials: true,
    });
    if (response?.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const preparePaymentData = async ({
  authorizationToken,
  basketProducts,
  payFinalOrderAmount,
  isWalletApplied,
  basketDiscountForExistingVipUser,
  orderSummary,
  promoCode,
  sessionId,
  vipOptIn,
  vipOptInType,
  vipSummary,
}) => {
  return {
    adminFeeApplied: basketProducts.some((product) => product.adminFee > 0),
    authorization_token: authorizationToken.authorization_token,
    cashAmount: payFinalOrderAmount,
    location: getUserLocationShortName(),
    orderLines: compact(
      map(basketProducts, (product) => {
        if (product.quantity <= 0 || product.soldOut) return null;
        const deliveryOptionType = getDeliveryOptionType({
          orderSummary,
          product,
        });
        const orderLinesPayload = getOrderLinesPayload({
          deliveryOptionType,
          product,
        });

        if (product.numberOfNights)
          orderLinesPayload.numberOfNights = product.numberOfNights;
        if (product.travel) {
          orderLinesPayload.travel = product.travel;
        }

        if (product.checkInDate) {
          orderLinesPayload.calendarCheckInDate = formatDateForApi(
            product.checkInDate,
          );
        }
        if (product.checkOutDate) {
          orderLinesPayload.calendarCheckOutDate = formatDateForApi(
            product.checkOutDate,
          );
        }

        return orderLinesPayload;
      }),
    ),
    postageDiscountAmount: orderSummary.totalPostageDiscount || null,
    postageOnCheckoutRule: orderSummary.postageOnCheckoutRule || null,
    postageOnRedemption: orderSummary.postageOnRedemption || null,
    promoCode: promoCode.value,
    purchaseSource: sessionSource || null,
    sessionId,
    vipDiscountApplied:
      Boolean(basketDiscountForExistingVipUser) ||
      (vipSummary?.vipOptInSubscription?.vipOptInBasketDiscount > 0 &&
        vipOptIn === true),
    vipOptedIn: vipOptIn || false,
    vipOptInType: vipOptInType || '',
    walletAmount: await getWalletAmount(orderSummary),
    walletUsed: isWalletApplied,
  };
};

export const setOrderlines = ({
  isVipOptIn,
  products,
  orderSummary,
  vipPrice,
  vipSummary,
}) => {
  const orderlines = products.map((deal) => {
    let price = deal.payDeposit ? deal.depositProduct.price : deal.price;
    const id = deal.payDeposit ? deal.depositProduct.id : deal.id;
    if (deal?.giftPack) {
      price = deal.originalPrice + deal.postagePrice;
    }

    return {
      image_url: deal.imageUrl,
      name: deal.title,
      product_url: deal.url,
      quantity: deal.quantity,
      reference: id,
      total_amount: deal?.giftPack
        ? Math.round(deal.finalProductAmount * 100)
        : Math.round(
            (deal.totalProductAmount - deal.promoCodeDiscountAmount) * 100,
          ),
      total_discount_amount: Math.round(deal.promoCodeDiscountAmount * 100),
      type: 'digital',
      unit_price: Math.round(price * 100),
    };
  });
  if (isVipOptIn && vipPrice) {
    orderlines.push({
      name: 'VIP',
      quantity: 1,
      total_amount: Math.round(vipPrice * 100),
      type: 'digital',
      unit_price: Math.round(vipPrice * 100),
    });
    if (vipSummary?.vipOptInSubscription?.vipOptInBasketDiscount) {
      orderlines.push({
        name: 'Vip Discount',
        quantity: 1,
        total_amount: Math.round(
          vipSummary.vipOptInSubscription.vipOptInBasketDiscount * -100,
        ),
        type: 'discount',
        unit_price: Math.round(
          vipSummary.vipOptInSubscription.vipOptInBasketDiscount * -100,
        ),
      });
    }
  }
  if (orderSummary.totalPostageAmount) {
    orderlines.push({
      name: 'postage',
      quantity: 1,
      total_amount: Math.round(orderSummary.totalPostageAmount * 100),
      type: 'shipping_fee',
      unit_price: Math.round(orderSummary.totalPostageAmount * 100),
    });
  }
  if (orderSummary.totalAdminFee) {
    orderlines.push({
      name: 'admin fee',
      quantity: 1,
      total_amount: Math.round(orderSummary.totalAdminFee * 100),
      type: 'surcharge',
      unit_price: Math.round(orderSummary.totalAdminFee * 100),
    });
  }
  if (orderSummary.totalWalletAmount) {
    orderlines.push({
      name: 'Wallet Credit',
      quantity: 1,
      total_amount: Math.round(orderSummary.totalWalletAmount * -100),
      type: 'discount',
      unit_price: Math.round(orderSummary.totalWalletAmount * -100),
    });
  }
  if (vipSummary?.basketDiscountForExistingVipUser) {
    orderlines.push({
      name: 'Vip Discount',
      quantity: 1,
      total_amount: Math.round(
        vipSummary.basketDiscountForExistingVipUser * -100,
      ),
      type: 'discount',
      unit_price: Math.round(
        vipSummary.basketDiscountForExistingVipUser * -100,
      ),
    });
  }

  return orderlines;
};

export const setAttachment = ({ products, extraMerchantData }) => {
  const attachment = {
    body: {},
    content_type: 'application/vnd.klarna.internal.emd-v2+json',
  };
  const voucher = [];
  const marketplace_seller_info = [];

  products.forEach((deal) => {
    const startDate = dayjs()
      .startOf('day')
      .toISOString()
      .replace(/.000Z/, 'Z');
    const endDate = dayjs()
      .add(3, 'months')
      .endOf('day')
      .toISOString()
      .replace(/.999Z/, 'Z');
    const extraOrderline = {
      end_time: endDate,
      start_time: startDate,
      voucher_company: deal.business,
      voucher_name: deal.title,
    };
    const extraMerchantInfo = {
      account_last_modified: {
        email: extraMerchantData.elm,
        password: extraMerchantData.plm,
      },
      account_registration_date: extraMerchantData.ard,
      product_category: deal.category,
      product_name: deal.title,
    };
    voucher.push(extraOrderline);
    marketplace_seller_info.push(extraMerchantInfo);
  });
  attachment.body = JSON.stringify({ marketplace_seller_info, voucher });

  return attachment;
};
